import { BaseClient } from 'clients/BaseClient/BaseClient';

import { hosts } from 'config';

import {
  AtsCredentialsOverviewHandler,
  AtsDropdownHandler,
  AtsFieldsHandler,
  AtsPhasesHandler,
  CreateAtsCredentialsHandler,
  DeleteAtsCredentialsHandler,
  GetDepartmentsHandler,
  OAuth2AtsCredentialHandler,
  PullStatusesHandler,
  ToggleAtsCredentialsHandler,
  UpdateAtsCredentialsHandler,
  ValidateAtsConnectionResponseOk,
  type AtsSelectionItem,
  type AtsSelectionType,
  type GetAvailableAtsToPullStatuses,
  type GetAvailableSelectionsHandler,
  type GetSelectedSelectionsHandler,
  type GetStatusesHandler,
  type SaveSelectedSelectionsHandler,
  type SaveStatusesHandler,
} from './types';

export class ATSIntegrationsClient extends BaseClient {
  constructor() {
    super();
  }

  getAll: AtsCredentialsOverviewHandler = () => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credentials/overview/`,
      method: 'GET',
    });
  };

  getFields: AtsFieldsHandler = (params) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats/fields`,
      method: 'GET',
      params,
    });
  };

  getPhases: AtsPhasesHandler = () => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats/phases`,
      method: 'GET',
    });
  };

  getDropdown: AtsDropdownHandler = () => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats/`,
      method: 'GET',
    });
  };

  patch: UpdateAtsCredentialsHandler = ({ id, ...data }) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credentials/${id}/`,
      method: 'PATCH',
      data,
    });
  };

  post: CreateAtsCredentialsHandler = (data) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credentials/`,
      method: 'POST',
      data,
    });
  };

  hasValidATSConnected = async () =>
    this.request<ValidateAtsConnectionResponseOk>({
      url: `${hosts.atsGatewayApi()}/users/validate_ats_connection`,
      method: 'GET',
    });

  delete: DeleteAtsCredentialsHandler = (id) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credentials/${id}/`,
      method: 'DELETE',
    });
  };

  toggle: ToggleAtsCredentialsHandler = ({ id, is_active }) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credential_user/`,
      method: 'POST',
      data: {
        ats_credential_id: id.toString(),
        is_active,
      },
    });
  };

  oauth2: OAuth2AtsCredentialHandler = ({ code, atsCredential, atsName }) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_credentials/oauth2/connect/${atsName}/`,
      method: 'POST',
      data: {
        code,
        ats_credential: atsCredential,
      },
    });
  };

  getDepartments: GetDepartmentsHandler = (email) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/users/${email}/departments/`,
      method: 'GET',
    });
  };

  getStatuses: GetStatusesHandler = (tenantId, atsName, type) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/${tenantId}/?ats=${atsName}&status_type=${type}`,
      method: 'GET',
    });
  };

  saveStatuses: SaveStatusesHandler = (tenantId, statuses) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/${tenantId}/`,
      method: 'POST',
      data: statuses,
    });
  };

  pullApplicationStatuses: PullStatusesHandler = (tenantId, atsName) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/applications/pull`,
      method: 'POST',
      data: {
        tenant_id: tenantId,
        ats_name: atsName,
      },
    });
  };

  pullCandidateStatuses: PullStatusesHandler = (tenantId, atsName) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/candidates/pull`,
      method: 'POST',
      data: {
        tenant_id: tenantId,
        ats_name: atsName,
      },
    });
  };

  getAvailableAtsToPullStatuses: GetAvailableAtsToPullStatuses = () => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats/robin_resume/`,
      method: 'GET',
    });
  };

  refreshCandidateStatuses: PullStatusesHandler = (tenantId, atsName) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/candidates/refresh`,
      method: 'POST',
      data: {
        tenant_id: tenantId,
        ats_name: atsName,
      },
    });
  };

  refreshApplicationStatuses: PullStatusesHandler = (tenantId, atsName) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_statuses/applications/refresh`,
      method: 'POST',
      data: {
        tenant_id: tenantId,
        ats_name: atsName,
      },
    });
  };

  private getAvailableSelections = (tenantId: number, atsName: string, type: AtsSelectionType) => {
    return this.request<AtsSelectionItem[]>({
      url: `${hosts.atsGatewayApi()}/ats_selections/${tenantId}/available/?ats=${atsName}&type=${type}`,
      method: 'GET',
    });
  };

  private getSelectedSelections = (tenantId: number, atsName: string, type: AtsSelectionType) => {
    return this.request<AtsSelectionItem[]>({
      url: `${hosts.atsGatewayApi()}/ats_selections/${tenantId}/selected/?ats=${atsName}&type=${type}`,
      method: 'GET',
    });
  };

  private saveSelectedSelections = (
    tenantId: number,
    atsName: string,
    type: AtsSelectionType,
    items: AtsSelectionItem[],
  ) => {
    return this.request({
      url: `${hosts.atsGatewayApi()}/ats_selections/${tenantId}/selected/?ats=${atsName}&type=${type}`,
      method: 'POST',
      data: items,
    });
  };

  getAvailableJobStatuses: GetAvailableSelectionsHandler = (tenantId, atsName) =>
    this.getAvailableSelections(tenantId, atsName, 'job_status');

  getSelectedJobStatuses: GetSelectedSelectionsHandler = (tenantId, atsName) =>
    this.getSelectedSelections(tenantId, atsName, 'job_status');

  saveSelectedJobStatuses: SaveSelectedSelectionsHandler = (tenantId, atsName, items) =>
    this.saveSelectedSelections(tenantId, atsName, 'job_status', items);

  getAvailableJobLabels: GetAvailableSelectionsHandler = (tenantId, atsName) =>
    this.getAvailableSelections(tenantId, atsName, 'job_label');

  getSelectedJobLabels: GetSelectedSelectionsHandler = (tenantId, atsName) =>
    this.getSelectedSelections(tenantId, atsName, 'job_label');

  saveSelectedJobLabels: SaveSelectedSelectionsHandler = (tenantId, atsName, items) =>
    this.saveSelectedSelections(tenantId, atsName, 'job_label', items);

  getAvailableCandidateStatuses: GetAvailableSelectionsHandler = (tenantId, atsName) =>
    this.getAvailableSelections(tenantId, atsName, 'candidate_status');

  getSelectedCandidateStatuses: GetSelectedSelectionsHandler = (tenantId, atsName) =>
    this.getSelectedSelections(tenantId, atsName, 'candidate_status');

  saveSelectedCandidateStatuses: SaveSelectedSelectionsHandler = (tenantId, atsName, items) =>
    this.saveSelectedSelections(tenantId, atsName, 'candidate_status', items);

  getAvailableCandidateLabels: GetAvailableSelectionsHandler = (tenantId, atsName) =>
    this.getAvailableSelections(tenantId, atsName, 'candidate_label');

  getSelectedCandidateLabels: GetSelectedSelectionsHandler = (tenantId, atsName) =>
    this.getSelectedSelections(tenantId, atsName, 'candidate_label');

  saveSelectedCandidateLabels: SaveSelectedSelectionsHandler = (tenantId, atsName, items) =>
    this.saveSelectedSelections(tenantId, atsName, 'candidate_label', items);
}

export const atsIntegrationsClient = new ATSIntegrationsClient();
